"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, SlidersHorizontal as Sliders, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  number: "01",
  title: "In-Depth SEO & Technical Audit",
  shortTitle: "Audit",
  description: "I personally analyze your entire site infrastructure, crawlability, indexation, and content performance to expose hidden bottlenecks blocking your search rankings.",
  details: ["Deep technical crawl & log review", "Keyword & search intent mapping", "Prioritized technical roadmap"],
  icon: Search
}, {
  number: "02",
  title: "On-Page & Content Refinement",
  shortTitle: "Optimization",
  description: "I optimize page structures, titles, and internal link flows while rewriting key landing pages to rank higher and convert qualified leads.",
  details: ["Heading & metadata strategy", "Content gap analysis", "Internal linking architecture"],
  icon: Sliders
}, {
  number: "03",
  title: "Citations & Local / Entity Signals",
  shortTitle: "Citations",
  description: "I build consistent NAP profiles across trusted industry directories to establish geographical relevance and strengthen entity authority.",
  details: ["Citation audit & cleanup", "Local directory verification", "Entity relationship building"],
  icon: MapPin
}, {
  number: "04",
  title: "Reviews & Trust Architecture",
  shortTitle: "Reviews",
  description: "I implement structured review strategies that turn real client feedback into powerful conversion assets and search engine trust signals.",
  details: ["Review acquisition strategy", "Schema markup integration", "Reputation management"],
  icon: Star
}, {
  number: "05",
  title: "Schema & AI Search Optimization",
  shortTitle: "Schema",
  description: "I deploy custom JSON-LD schema markup so Google, ChatGPT, and Perplexity clearly comprehend your core business services and expertise.",
  details: ["Custom JSON-LD schema", "AI Search Engine compatibility", "Rich snippet optimization"],
  icon: Code2
}, {
  number: "06",
  title: "Transparent Reporting & Strategy Iteration",
  shortTitle: "Reporting",
  description: "No fluff or vanity metrics. I deliver clear monthly insights on organic traffic growth, keyword rankings, and direct commercial impact.",
  details: ["Direct consultant updates", "Custom conversion tracking", "Ongoing strategy adjustments"],
  icon: BarChart3
}];
export default function Methodology() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".step-card", {
        y: 35
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".step-card", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="methodologyprocessflow" data-nav="light" className="bg-background relative py-20 lg:py-28 overflow-hidden">
      {/* Background Glow Accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/40 blur-3xl opacity-70" />
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-24 -left-24 size-96 rounded-full bg-secondary/30 blur-3xl opacity-60" />

      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-20">
          <Badge variant="secondary" className="mb-4 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border border-accent/20">
            <Sparkles className="size-3.5 mr-1 text-accent" />
            My Hands-On Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground font-serif tracking-tight leading-tight">
            A Proven 6-Step SEO Methodology Built for Sustainable Search Growth
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            You work directly with me. Here is the step-by-step strategy I use to transform search visibility, attract qualified leads, and build long-term search engine authority for your business.
          </p>
        </div>

        {/* Process Flow Cards */}
        <div className="relative">
          {/* Subtle connecting guideline on desktop */}
          <div aria-hidden="true" className="hidden lg:block absolute top-1/2 left-8 right-8 h-0.5 bg-secondary -translate-y-1/2 z-0" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-8 relative z-10">
            {steps.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.number} data-index={i} className="step-card h-full" style={{
              opacity: 0
            }}>
                  <Card className="h-full border border-border bg-card shadow-md hover:shadow-xl transition-all duration-300 relative group flex flex-col justify-between overflow-hidden">
                    {/* Top Accent Line */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary opacity-80 group-hover:opacity-100 transition-opacity" />

                    <div>
                      <CardHeader className="pt-8 pb-4">
                        <div className="flex items-center justify-between mb-4">
                          {/* Phase Number Badge */}
                          <div className="size-12 rounded-xl bg-primary text-primary-foreground font-bold text-lg flex items-center justify-center shadow-sm">
                            {step.number}
                          </div>

                          {/* Icon Container */}
                          <div className="p-2.5 rounded-lg bg-muted text-foreground border border-border group-hover:border-accent/40 transition-colors">
                            <IconComponent className="size-5 text-primary" />
                          </div>
                        </div>

                        <CardTitle className="text-xl font-bold font-serif text-card-foreground group-hover:text-primary transition-colors">
                          {step.title}
                        </CardTitle>
                        <CardDescription className="text-sm font-medium text-muted-foreground mt-1">
                          Phase {step.number} • {step.shortTitle}
                        </CardDescription>
                      </CardHeader>

                      <CardContent className="space-y-4 pb-6">
                        <p className="text-sm text-foreground/80 leading-relaxed font-default">
                          {step.description}
                        </p>

                        <div className="pt-2 border-t border-border/60">
                          <ul className="space-y-2">
                            {step.details.map((detail, idx) => <li key={idx} className="flex items-start text-xs font-medium text-muted-foreground">
                                <CheckCircle2 className="size-3.5 text-accent shrink-0 mt-0.5 mr-2" />
                                <span>{detail}</span>
                              </li>)}
                          </ul>
                        </div>
                      </CardContent>
                    </div>

                    {/* Footer Deliverable Note */}
                    <div className="px-6 pb-6 pt-2 flex items-center text-xs font-semibold text-primary group-hover:text-accent transition-colors">
                      <span className="mr-1">Key Deliverable</span>
                      <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                    </div>
                  </Card>
                </div>;
          })}
          </div>
        </div>

        {/* Section Call to Action Banner */}
        <div className="mt-16 lg:mt-20 bg-muted border border-border rounded-2xl p-8 sm:p-10 text-center max-w-4xl mx-auto shadow-sm relative overflow-hidden">
          <div aria-hidden="true" className="absolute -right-12 -bottom-12 size-48 rounded-full bg-secondary/50 blur-2xl" />
          
          <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-3 relative z-10">
            Ready to build a customized SEO strategy for your business?
          </h3>
          <p className="text-muted-foreground text-sm sm:text-base max-w-2xl mx-auto mb-8 relative z-10 font-default">
            No account managers or delegated junior team members. You work directly with me from day one. Let us analyze your current search standing and map out your path to sustainable growth.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 relative z-10">
            <Button size="lg" className="w-full sm:w-auto px-8 font-semibold shadow-md cursor-pointer" asChild>
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto px-8 cursor-pointer" asChild>
              <Link to="/CaseStudiesPage">
                Explore My Case Studies
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}